import { AccessContext } from '@4r/module-common-authorization';
import React, { useContext, useMemo } from 'react';
import { HubConfigurationBuilder, HubProvider, HubAuthorization, HubDefinition } from '@4r/module-common-signalr';
import { useConfig } from '@4r/module-common-mf-app';
import { UserOrdersHub } from '../hubs/UserOrdersHub';
import { useGlobalState } from '../state/Store';
import { PageStatus } from '../state/Actions';
import { AuthContext } from '../authentication/AuthProvider';
import PermissionType from '../authorization/PermissionType';
import { CurrentGeocoordinatesHub } from '../hubs/CurrentGeocoordinatesHub';
import { TelemetryEvent, trackEvent } from '../observability/telemetry';

/**
 * Reports hub connection lifecycle as structured telemetry.
 *
 * SignalR writes every disconnect and failed negotiation to console.error, which Faro's console
 * instrumentation converts into an exception. For an app that is expected to lose connectivity,
 * that produced a constant stream of "exceptions" — enough to bury a genuine crash. Those console
 * lines are filtered out in observability/faro.ts; this is what replaces them, and it says more:
 * which hub, which transition, rather than a stringified error.
 */
// The module declares HubEventHandlers but does not re-export it from its index, so the type is
// derived from HubDefinition — which is exported — rather than deep-importing from dist/.
type HubEventHandlers = NonNullable<HubDefinition['handlers']>;

const hubTelemetry = (hub: string): HubEventHandlers => ({
	onConnectionStarted: () => trackEvent(TelemetryEvent.SignalRStateChanged, { hub, state: 'connected' }),
	onConnectionFailed: () => trackEvent(TelemetryEvent.SignalRStateChanged, { hub, state: 'connection-failed' }),
	onReconnecting: () => trackEvent(TelemetryEvent.SignalRStateChanged, { hub, state: 'reconnecting' }),
	onReconnected: () => trackEvent(TelemetryEvent.SignalRStateChanged, { hub, state: 'reconnected' }),
	onClose: () => trackEvent(TelemetryEvent.SignalRStateChanged, { hub, state: 'closed' }),
});

const HubConfigurationProvider: React.FunctionComponent = ({ children }) => {
	const settings = useConfig();
	const auth = useContext(AuthContext);
	const access = useContext(AccessContext);
	const [onlineStatus] = useGlobalState('online');
	const hasSendCurrentGeoCoordinates = access.has(PermissionType.SendCurrentGeoCoordinates);
	const hasViewScheduledWorkOrders = access.has(PermissionType.ViewScheduledWorkOrders);

	const configuration = useMemo(() => {
		const hubsEnabled =
			(onlineStatus !== PageStatus.offline && auth && auth.isAuthenticated && settings && hasSendCurrentGeoCoordinates) || false;

		const hubauthorization: HubAuthorization = {
			updateCurrentGeoCoordinates: hasSendCurrentGeoCoordinates,
			notifyBookingChanged: hasViewScheduledWorkOrders,
		};

		const builder = new HubConfigurationBuilder();
		const config = builder
			.add(
				CurrentGeocoordinatesHub,
				`${settings?.API_URL}/hubs/current-geocoordinates`,
				hubauthorization,
				hasSendCurrentGeoCoordinates,
				hubTelemetry('CurrentGeocoordinatesHub'),
			)
			.add(
				UserOrdersHub,
				`${settings?.API_URL}/hubs/user-orders-updates`,
				hubauthorization,
				hasViewScheduledWorkOrders,
				hubTelemetry('UserOrdersHub'),
			)
			.registerAccessTokenFactory(() => auth.authService?.getAccessToken() ?? null)
			.enableHubs(hubsEnabled)
			// Debug mode drops SignalR's own logging to LogLevel.Information, which the console
			// instrumentation then ships as logs. Useful locally, pure volume everywhere else.
			.setDebugMode(process.env.REACT_APP_HOST_TYPE === 'local')
			.build();

		return config;
	}, [settings, auth, access, hasSendCurrentGeoCoordinates, hasViewScheduledWorkOrders, onlineStatus]);

	return <HubProvider configuration={configuration}>{children}</HubProvider>;
};

export default HubConfigurationProvider;
